/* eslint-disable no-param-reassign */
import Table, { SortDirection, Header } from 'bam-node-savant-table/table-ts.ts'
import attackZone from 'bam-node-bdata-charts/chart.attack-zone'
import { slideUp, slideDown } from './utils/leaderboard-drawer-helpers'
import initStaticHeaders from './utils/static-headers'
import concatGroupedHeaders from './utils/concat-grouped-headers'

import { populateUrl } from './utils/leaderboard-filter-helpers'
import { sampleZoneData } from './abs/utils.ts'
import {
    AbsChallengeRow,
    AbsChallengeDrawerRow,
} from './swing-path/types/globals.ts'
import * as viz from './abs-zone-viz.ts'

import {
    groupHeaders,
    getPitcherHeaders,
    getHeaders,
    getUmpireHeaders,
    getSingleChallengeHeaders,
    getHeadersSummary,
    getSecondaryHeaders,
    getSecondaryHeadersSummary,
} from './abs/headers.ts'

let vizDataStored: AbsChallengeDrawerRow[]
let isExpanded = false

// Globals
type Mode = 'for' | 'against' | 'forcatchers' | 'againstcatchers'
type Count = 'chal' | 'runs'

type ServerParams = {
    level?: string
    type:
        | 'league'
        | 'batter'
        | 'pitcher'
        | 'catcher'
        | 'fielder'
        | 'batting-team'
        | 'pitching-team'
        | 'catching-team'
        | 'fielding-team'
        | 'umpire'
    secondaryGroupBy: string[]
    seasonStart: number
    seasonEnd: number
    year?: number
    challengeType?: string
    dataMode: Mode
    dataCount: Count
    sort?: {
        column: string
        direction: string
    }
    fielderMode?: 'all' | 'as-challenger'
}

type TableOpts = {
    id: string
    repeatHeader?: number
    headers: Header<AbsChallengeRow>[]
    secondaryHeaders?: {
        name: string
        colspan: number
        headerClass: string
    }[]
    highlightFalsy?: boolean
    highlightNullish?: boolean
    data: AbsChallengeRow[]
    defaultSort?: keyof AbsChallengeRow
    initialSortDirection?: SortDirection
    sortClass: string
    showRank: boolean
    uniqueId?: keyof AbsChallengeRow
    subDataId?: keyof AbsChallengeRow
    handleUrlUpdates: {
        enabled: boolean
        pagination: boolean
        filters: boolean
    }
    search?: { enabled: boolean; columnsToSearch?: (keyof AbsChallengeRow)[] }
    drawCallback?: () => void
}

declare const absData: AbsChallengeRow[]
declare const selectedView: string
declare const selCount: string
declare const serverParams: ServerParams
declare const isProd: boolean

const tableDivId = 'abs_table'
const selectorId = 'abs-mode-selector'
const countSelectorId = 'abs-count-selector'
let selectedMode: Mode = (selectedView as Mode) || 'for'
let selectedCount: Count = (selCount as Count) || 'chals'

const batterTypes = ['batter', 'batting-team']
const isPlayerType = ['batter', 'catcher', 'pitcher'].includes(
    String(serverParams.challengeType)
)

const tableMap: Record<string, Table<AbsChallengeDrawerRow>> = {}

const domColumns: {
    for: NodeListOf<Element> | null
    against: NodeListOf<Element> | null
} = {
    for: null,
    against: null,
}
const isSummaryView = ['team-summary', 'league'].includes(
    String(serverParams.challengeType)
)
const $table = document.getElementById(tableDivId)

if ($table) {
    $table.dataset.expanded = 'false'
}
let tableVar: Table<AbsChallengeRow>

function refreshSecondarySelector() {
    const selectorElem = document.getElementById(
        selectorId
    ) as HTMLSelectElement
    selectorElem.value = selectedMode

    const countSelectorElem = document.getElementById(
        countSelectorId
    ) as HTMLSelectElement
    countSelectorElem.value = selectedCount
}

function addListeners() {
    document.getElementById(tableDivId)?.addEventListener('click', (e) => {
        const targetElem = e.target as Element

        if (!targetElem) {
            return
        }

        const expandButton = targetElem.closest('#btn-expected')

        if (!expandButton || targetElem.id !== 'btn-expected') {
            return
        }

        isExpanded = !isExpanded

        if ($table?.dataset.expanded === 'false') {
            $table.dataset.expanded = 'true'
        } else if ($table?.dataset.expanded === 'true') {
            $table.dataset.expanded = 'false'
        }

        if (isSummaryView) {
            const headersSummary = getHeadersSummary(
                isExpanded,
                String(serverParams.level)
            )
            const headersSummaryToUse = isExpanded
                ? headersSummary
                : headersSummary.filter((d) => !d.expanded)

            const headersSummaryWithGrouped = concatGroupedHeaders(
                headersSummaryToUse,
                groupHeaders,
                'player_name'
            )

            tableVar.updateHeaders(
                headersSummaryWithGrouped,
                getSecondaryHeadersSummary(
                    selectedCount,
                    isExpanded,
                    serverParams.secondaryGroupBy.filter(
                        (d) => d !== 'api_game_date_month_mm'
                    ).length,
                    String(serverParams.level)
                )
            )
        } else {
            const newHeaders = isExpanded
                ? getHeaders(String(serverParams.level))
                : getHeaders(String(serverParams.level)).filter(
                      (d) => !d.expanded
                  )

            const newHeadersWithGrouped = concatGroupedHeaders(
                newHeaders,
                groupHeaders,
                'player_name'
            )

            const newSecondaryHeaders = getSecondaryHeaders(
                selectedMode,
                selectedCount,
                isExpanded,
                serverParams.secondaryGroupBy.filter(
                    (d) => d !== 'api_game_date_month_mm'
                ).length,
                String(serverParams.level)
            )
            tableVar.updateHeaders(newHeadersWithGrouped, newSecondaryHeaders)
        }
    })

    document.getElementById('btn-update')?.addEventListener('click', () => {
        const urlObj: {
            year: string
            challengeType: string
            gameType: string
            level: string
            minChal: string
            minOppChal: string
            fielderMode?: string
            breakeven?: string
        } = {
            year: (document.getElementById('ddlSeason') as HTMLSelectElement)
                ?.value,
            challengeType: (
                document.getElementById('ddlChalType') as HTMLSelectElement
            )?.value,
            gameType: (
                document.getElementById('ddlGameType') as HTMLSelectElement
            )?.value,
            level: (document.getElementById('ddlLevel') as HTMLSelectElement)
                ?.value,
            minChal: (
                document.getElementById('ddlMinChal') as HTMLSelectElement
            )?.value,
            minOppChal: (
                document.getElementById('ddlMinOppChal') as HTMLSelectElement
            )?.value,
            breakeven: (
                document.getElementById('ddlBreakEven') as HTMLSelectElement
            )?.value,
        }

        if (
            ['catcher', 'pitcher'].includes(String(serverParams.challengeType))
        ) {
            urlObj.fielderMode = (
                document.getElementById('ddlFielderMode') as HTMLSelectElement
            )?.value
        }

        window.location.href = populateUrl(urlObj)
    })

    document.getElementById('btn-reset')?.addEventListener('click', () => {
        window.location.href = '/leaderboard/abs-challenges'
    })

    document.getElementById('btn-csv')?.addEventListener('click', () => {
        window.location.href = `${populateUrl({
            year: (document.getElementById('ddlSeason') as HTMLSelectElement)
                ?.value,
            challengeType: (
                document.getElementById('ddlChalType') as HTMLSelectElement
            )?.value,
            level: (document.getElementById('ddlLevel') as HTMLSelectElement)
                ?.value,
            minChal: (
                document.getElementById('ddlMinChal') as HTMLSelectElement
            )?.value,
            minOppChal: (
                document.getElementById('ddlMinOppChal') as HTMLSelectElement
            )?.value,
        })}&csv=true`
    })

    document.getElementById(tableDivId)?.addEventListener('change', (e) => {
        const $target = e.target as HTMLSelectElement
        if ($target.id !== selectorId && $target.id !== countSelectorId) {
            return
        }

        const url = new URL(window.location.href)

        if ($target.id === selectorId) {
            if ($table) {
                $table.dataset.mode = $target.value
            }

            url.searchParams.set('dataMode', $target.value)
            selectedMode = $target.value as Mode
        } else if ($target.id === countSelectorId) {
            if ($table) {
                $table.dataset.count = $target.value
            }

            url.searchParams.set('dataCount', $target.value)
            selectedCount = $target.value as Count
        }

        window.history.replaceState('', '', url.toString())
        const extraCols = serverParams.secondaryGroupBy.filter(
            (d) => d !== 'api_game_date_month_mm'
        ).length

        let newSortColumn

        if (!isSummaryView) {
            if (!isPlayerType) {
                newSortColumn = (
                    selectedCount === 'runs'
                        ? `net_chal_gained_runs${
                              selectedMode === 'for' ? '' : '_against_proxy'
                          }`
                        : `net_net_chal${
                              selectedMode === 'for' ? '' : '_against_proxy'
                          }`
                ) as keyof AbsChallengeRow
            } else {
                newSortColumn = `net_net_${selectedCount}${
                    selectedMode === 'for' ? '' : '_against_proxy'
                }` as keyof AbsChallengeRow
            }
        } else {
            // newSortColumn = `${
            //     selectedCount === 'runs' ? 'runs' : 'overturns'
            // }_vs_exp_total` as keyof AbsChallengeRow

            newSortColumn = (
                selectedCount === 'runs'
                    ? 'net_chal_gained_runs_total'
                    : 'overturns_vs_exp_total'
            ) as keyof AbsChallengeRow
        }

        tableVar.currentSortColumn = [{ field: newSortColumn, state: 'desc' }]

        tableVar.secondaryHeaders = isSummaryView
            ? getSecondaryHeadersSummary(
                  selectedCount,
                  isExpanded,
                  extraCols,
                  String(serverParams.level)
              )
            : getSecondaryHeaders(
                  selectedMode,
                  selectedCount,
                  isExpanded,
                  extraCols,
                  String(serverParams.level)
              )

        tableVar.refreshTable()

        document
            .querySelectorAll(`.abs-${selectedCount}-${selectedMode}`)
            ?.forEach((el) => {
                el.classList.remove('highlight-flash')
                el.classList.add('highlight-flash')
            })

        document
            .querySelectorAll(`.abs-summary-${selectedCount}`)
            ?.forEach((el) => {
                el.classList.remove('highlight-flash')
                el.classList.add('highlight-flash')
            })
    })
}

function drawTable() {
    const width = document.getElementById(tableDivId)?.offsetWidth || 0
    let repeatHeader = 0
    if (width <= 768) {
        repeatHeader = 15
    }

    const currentParams = new URLSearchParams(window.location.search)
    const currentSort = currentParams.get('sortColumn') as keyof AbsChallengeRow
    const currentSortDir = currentParams.get('sortDirection') as SortDirection

    if (serverParams.challengeType === 'umpire' && !isProd) {
        const headers = getUmpireHeaders()
        const headersWithGrouped = concatGroupedHeaders(
            headers,
            groupHeaders,
            'umpire_name'
        )
        // eslint-disable-next-line no-new
        new Table({
            id: tableDivId,
            repeatHeader,
            headers: headersWithGrouped,
            highlightFalsy: true,
            highlightNullish: false,
            data: absData,
            defaultSort: currentSort || 'net_net_chal',
            initialSortDirection: currentSortDir || 'desc',
            sortClass: 'column-sort',
            showRank: true,
            uniqueId: 'uniqueId',
            subDataId: 'uniqueId',
            handleUrlUpdates: {
                enabled: true,
                pagination: true,
                filters: false,
            },
            search: { enabled: true, columnsToSearch: ['player_name'] },
        })
    } else {
        const extraCols = serverParams.secondaryGroupBy.filter(
            (d) => d !== 'api_game_date_month_mm'
        ).length

        if (isSummaryView) {
            const headersSummary = getHeadersSummary(
                isExpanded,
                String(serverParams.level)
            )
            const headersSummaryToUse = isExpanded
                ? headersSummary
                : headersSummary.filter((d) => !d.expanded)

            const headersSummaryToUseWithGrouped = concatGroupedHeaders(
                headersSummaryToUse,
                groupHeaders,
                'player_name'
            )

            // eslint-disable-next-line no-new
            tableVar = new Table({
                id: tableDivId,
                repeatHeader,
                headers: headersSummaryToUseWithGrouped,
                secondaryHeaders: getSecondaryHeadersSummary(
                    selectedCount,
                    isExpanded,
                    extraCols,
                    String(serverParams.level)
                ),
                highlightFalsy: true,
                highlightNullish: false,
                data: absData,
                defaultSort: currentSort || 'overturns_vs_exp_total',
                initialSortDirection: currentSortDir || 'desc',
                sortClass: 'column-sort',
                showRank: true,
                uniqueId: 'uniqueId',
                subDataId: 'uniqueId',
                handleUrlUpdates: {
                    enabled: true,
                    pagination: true,
                    filters: false,
                },
                search: { enabled: true, columnsToSearch: ['player_name'] },
            })
        } else {
            let headersToUse

            const useSimplePitcherHeaders =
                serverParams.challengeType === 'pitcher' &&
                serverParams.fielderMode === 'as-challenger'

            if (useSimplePitcherHeaders) {
                headersToUse = getPitcherHeaders(String(serverParams.level))
            } else {
                headersToUse = isExpanded
                    ? getHeaders(String(serverParams.level))
                    : getHeaders(String(serverParams.level)).filter(
                          (d) => !d.expanded
                      )
            }

            const headersToUseWithGrouped = concatGroupedHeaders(
                headersToUse,
                groupHeaders,
                'player_name'
            )

            let initSort =
                selectedMode === 'for'
                    ? 'net_net_chal'
                    : 'net_net_chal_against_proxy'

            if (useSimplePitcherHeaders) {
                initSort = 'n_overturns'
            }

            const tableOpts: TableOpts = {
                id: tableDivId,
                repeatHeader,
                headers: headersToUseWithGrouped,
                highlightFalsy: true,
                highlightNullish: false,
                data: absData,
                defaultSort: currentSort || initSort,
                initialSortDirection: currentSortDir || 'desc',
                sortClass: 'column-sort',
                showRank: true,
                uniqueId: 'uniqueId',
                subDataId: 'uniqueId',
                handleUrlUpdates: {
                    enabled: true,
                    pagination: true,
                    filters: false,
                },
                search: { enabled: true, columnsToSearch: ['player_name'] },
            }

            if (!useSimplePitcherHeaders) {
                tableOpts.secondaryHeaders = getSecondaryHeaders(
                    selectedMode,
                    selectedCount,
                    isExpanded,
                    extraCols,
                    String(serverParams.level)
                )
                tableOpts.drawCallback = refreshSecondarySelector
            }

            // eslint-disable-next-line no-new
            tableVar = new Table<AbsChallengeRow>(tableOpts)
        }

        initStaticHeaders(tableDivId, absData)
        cacheDOMColumns()
    }
}

function cacheDOMColumns() {
    domColumns.for = document.querySelectorAll('.abs-chal-for')
    domColumns.against = document.querySelectorAll('.abs-chal-against')
}

$(document).ready(() => {
    if (absData && absData.length > 0) {
        drawTable()
    }

    if (serverParams.challengeType === 'league') {
        const tableElem = document.querySelector(`#${tableDivId}`)
        const innerTableElem = tableElem?.querySelector('.table-savant')
        innerTableElem?.setAttribute('style', 'padding-bottom: 100px;')
    }

    addListeners()
    addRowListeners()
})

async function getDrawerPitches(params: URLSearchParams, id: string) {
    let url = `/leaderboard/services/abs/${id}`
    if (params.toString().length > 0) {
        url += `?${params.toString()}`
    }
    const { data } = await fetch(url).then((res) => res.json())
    return data
}

function addDrawerTabs($wrapperDiv: HTMLDivElement, idNum: string) {
    const isTeamSummary = serverParams.challengeType === 'team-summary'

    let drawerTabs = `
    <div class="drawer-tabs">
        <button data-tab="chal-for" class="tab-chal-for drawer-nav active" id="for_button_${idNum}" >
            Challenges Made${isTeamSummary ? ' (Batters)' : ''}
        </button>
        `

    if (serverParams.challengeType !== 'umpire') {
        drawerTabs += `<button data-tab="chal-against" class="tab-chal-against drawer-nav" id="against_button_${idNum}" >
            Challenges Against${isTeamSummary ? ' (Batters)' : ''}
        </button>
    ${isTeamSummary ? '' : '</div>'}
    `

        if (isTeamSummary) {
            drawerTabs += `<button data-tab="chal-for-catchers" class="tab-chal-for-catchers drawer-nav" id="forcatchers_button_${idNum}" >
                Challenges Made (Catchers)
            </button>
        `

            drawerTabs += `<button data-tab="chal-against-catchers" class="tab-chal-against-catchers drawer-nav" id="againstcatchers_button_${idNum}" >
                Challenges Against (Catchers)
            </button>
        </div>
        `
        }
    }

    $wrapperDiv.innerHTML = drawerTabs
}

function fillDropdownLeft(
    $leftWrapper: HTMLDivElement,
    id: string,
    vizData: AbsChallengeDrawerRow[],
    buttonType: Mode = 'for'
) {
    const $drawerHeader = document.createElement('h4')
    $drawerHeader.classList.add('drawer-inside-header')
    $drawerHeader.id = 'drawer-inside-header-elem'

    let entityType = 'Batter'

    if (serverParams.challengeType === 'team-summary') {
        switch (buttonType) {
            case 'against':
                entityType = 'Opp. Fielder'
                break
            case 'forcatchers':
                entityType = 'Fielder'
                break
            case 'againstcatchers':
                entityType = 'Opp. Batter'
                break
            default:
                break
        }
    } else if (batterTypes.includes(String(serverParams.challengeType))) {
        if (buttonType === 'against') {
            entityType = 'Opp. Fielder'
        }
    } else if (buttonType === 'for') {
        entityType = 'Fielder'
    } else {
        entityType = 'Opp. Batter'
    }

    $drawerHeader.innerText = `All Challenges Made by ${entityType}`

    const $drawerTable = document.createElement('div')
    $drawerTable.id = `challenge_table_${id}`
    $drawerTable.classList.add('drawer-inside-table')

    $leftWrapper.appendChild($drawerHeader)
    $leftWrapper.appendChild($drawerTable)

    // eslint-disable-next-line no-new
    const drawerTable = new Table({
        id: $drawerTable.id,
        data: vizData,
        sortClass: 'column-sort',
        rowClass: 'inner-table',
        headers: getSingleChallengeHeaders(buttonType),
        defaultSort: 'game_date',
        defaultSortDirection: 'asc',
        uniqueId: 'player_play_key',
        pagination: { enabled: false },
        search: { enabled: false },
        showRank: false,
    })

    tableMap[id] = drawerTable
}

function populateStrikeZoneViz(
    $wrapper: HTMLDivElement,
    id: string,
    vizData: AbsChallengeDrawerRow[],
    buttonType: Mode
) {
    const $svgWrapper = document.createElement('div')
    $svgWrapper.setAttribute('id', `all_viz_${id}`)
    $svgWrapper.classList.add('strike-zone-viz')
    $wrapper.appendChild($svgWrapper)

    const opts = {
        id: `all_viz_${id}`,
        svgId: `all_viz_${id}_svg`,
        show_footer: 'false',
        type: 'zScore',
        width: 300,
        height: 300,
        backgroundColor: '#ddd',
        segmentStrokeWidth: 1,
        segmentStrokeColor: '#fff',
        regions: {
            heart: true,
            shadow: true,
            chase: true,
            waste: true,
        },
        valueTags: false,
        tooltips: false,
        plate: true,
        footerText: '',
        zoneThickness: 1,
        zoneDash: `6,0`,
        zoneId: id,
        customHighlight: null,
        zone_data: sampleZoneData,
        isNewZone: true,
    }

    attackZone(opts)
    viz.plotPitches(`all_viz_${id}_svg`, id, vizData, {}, buttonType)
}

function respondToCheckbox(id: string, vizData: AbsChallengeDrawerRow[]) {
    const twoStrikesBox = document.getElementById(
        `two-strike_checkbox_${id}`
    ) as HTMLInputElement

    const threeBallsBox = document.getElementById(
        `three-ball_checkbox_${id}`
    ) as HTMLInputElement

    const flipsBox = document.getElementById(
        `flip_checkbox_${id}`
    ) as HTMLInputElement

    viz.plotPitches(`all_viz_${id}_svg`, id, vizData, {
        isTwoStrikes: twoStrikesBox?.checked,
        isThreeBalls: threeBallsBox?.checked,
        isFlip: flipsBox?.checked,
    })
}

const checkboxTextMap: Record<string, string> = {
    'two-strike': 'two-strike counts',
    'three-ball': 'three-ball counts',
    flip: 'K/BB flips',
}

function createCheckboxDiv(
    prefix: string,
    id: string
): [HTMLDivElement, HTMLInputElement] {
    const $checkboxDiv = document.createElement('div')
    $checkboxDiv.classList.add(`${prefix}-checkbox-div`)

    const $check = document.createElement('input')
    $check.classList.add('drawer-checkboxes')
    $check.setAttribute('id', `${prefix}_checkbox_${id}`)
    $check.setAttribute('type', 'checkbox')
    $check.setAttribute('name', `filter-${prefix}`)

    const $label = document.createElement('label')
    $label.innerHTML = ` Show only ${checkboxTextMap[prefix]}`
    $label.classList.add('drawer-checkbox-label')
    $label.setAttribute('for', `${prefix}_checkbox_${id}`)

    $checkboxDiv.appendChild($check)
    $checkboxDiv.appendChild($label)

    return [$checkboxDiv, $check]
}

function createCheckboxes(
    id: string,
    vizData: AbsChallengeDrawerRow[],
    buttonType: Mode
) {
    const $checkboxes = document.createElement('div')
    $checkboxes.classList.add('checkboxes-div')

    const [$twoStrikesDiv, $twoStrikeCheck] = createCheckboxDiv(
        'two-strike',
        id
    )
    const [$threeBallsDiv, $threeBallCheck] = createCheckboxDiv(
        'three-ball',
        id
    )
    const [$flipsDiv, $flipCheck] = createCheckboxDiv('flip', id)

    $twoStrikeCheck.addEventListener('click', () => {
        if ($twoStrikeCheck.checked) {
            tableMap[id].updateColumnFilter('pre_strike_count', 'eq', 2)
        } else {
            tableMap[id].removeColumnFilter('pre_strike_count')
        }
        respondToCheckbox(id, vizData)
    })

    $threeBallCheck.addEventListener('click', () => {
        if ($threeBallCheck.checked) {
            tableMap[id].updateColumnFilter('pre_ball_count', 'eq', 3)
        } else {
            tableMap[id].removeColumnFilter('pre_ball_count')
        }
        respondToCheckbox(id, vizData)
    })

    $flipCheck.addEventListener('click', () => {
        if ($flipCheck.checked) {
            tableMap[id].updateColumnFilter(
                `is_strikeout_or_walk_overturn${
                    buttonType.includes('against') ? '_against' : ''
                }`,
                'gt',
                0
            )
        } else {
            tableMap[id].removeColumnFilter(
                `is_strikeout_or_walk_overturn${
                    buttonType.includes('against') ? '_against' : ''
                }`
            )
        }
        respondToCheckbox(id, vizData)
    })

    $checkboxes.appendChild($twoStrikesDiv)
    $checkboxes.appendChild($threeBallsDiv)
    $checkboxes.appendChild($flipsDiv)

    return $checkboxes
}

function fillDropdownRight(
    $rightWrapper: HTMLDivElement,
    id: string,
    vizData: AbsChallengeDrawerRow[],
    buttonType: Mode
) {
    const $strikeZoneViz = document.createElement('div')
    $strikeZoneViz.classList.add('drawer-inside-viz')
    $strikeZoneViz.setAttribute('id', `drawer_${id}`)

    const $checkboxes = createCheckboxes(id, vizData, buttonType)

    $rightWrapper.appendChild($strikeZoneViz)
    $rightWrapper.appendChild($checkboxes)

    populateStrikeZoneViz($strikeZoneViz, id, vizData, buttonType)
}

function addRowListeners() {
    document.getElementById(tableDivId)?.addEventListener('mouseover', (e) => {
        const targetElem = e.target as Element

        if (!targetElem) {
            return
        }

        const row = targetElem.closest('.inner-table.default-table-row')

        if (!row) {
            return
        }

        const [rowUnique, playerPlayId] = row.id.split('-tr_')
        const uniqueId = rowUnique.split('table_')[1]
        const [playerId, playId] = playerPlayId.split('_')
        viz.handleTableHover(playId, playerId, uniqueId)
    })

    document.getElementById(tableDivId)?.addEventListener('mouseout', (e) => {
        const targetElem = e.target as Element

        if (!targetElem) {
            return
        }

        const row = targetElem.closest('.inner-table.default-table-row')

        if (!row) {
            return
        }

        const rowUnique = row.id.split('-tr_')[0]
        const uniqueId = rowUnique.split('table_')[1]

        viz.handleTableLeave(uniqueId)
    })
}

function fillDrawers(
    $wrapperDiv: HTMLDivElement,
    id: string,
    vizData: AbsChallengeDrawerRow[],
    buttonType: Mode,
    isTeamSummary: boolean
) {
    let currData
    const buttonToModeMap: Record<string, string> = {
        for: 'batter-for',
        against: 'batter-against',
        forcatchers: 'catcher-for',
        againstcatchers: 'catcher-against',
    }

    if (isTeamSummary) {
        currData = vizData?.filter(
            (d) => d.team_summary_mode === buttonToModeMap[buttonType]
        )
    } else {
        currData =
            buttonType === 'against'
                ? vizData?.filter((d) => d.against === true)
                : vizData?.filter((d) => d.against === false)
    }

    const $tabWrapper = document.createElement('div')
    $tabWrapper.classList.add('drawer-inside-wrapper')
    $tabWrapper.setAttribute('id', `inner_tab_${id}`)

    const $leftWrapper = document.createElement('div')
    $leftWrapper.classList.add('drawer-left-wrapper')
    $leftWrapper.setAttribute('id', `left_tab_${id}`)
    const $rightWrapper = document.createElement('div')
    $rightWrapper.classList.add('drawer-right-wrapper')
    $rightWrapper.setAttribute('id', `right_tab_${id}`)

    $wrapperDiv.appendChild($tabWrapper)
    $tabWrapper.appendChild($leftWrapper)
    $tabWrapper.appendChild($rightWrapper)

    fillDropdownLeft($leftWrapper, id, currData, buttonType)
    fillDropdownRight($rightWrapper, id, currData, buttonType)
}

function handleTabSwitch($button: HTMLElement) {
    const [button_type, idNum] = $button.id.split('_button_')
    const tabs = $button.closest('.drawer-tabs')

    const buttonMode = button_type as Mode

    if (!tabs) {
        return
    }

    if (!$button.classList.contains('active')) {
        tabs.querySelectorAll('.drawer-nav').forEach(($el) => {
            $el.classList.remove('active')
        })

        $button.classList.add('active')

        const $drawerWrapper = document.getElementById(
            `inner_tab_${idNum}`
        ) as HTMLDivElement

        $drawerWrapper.innerHTML = ''
        fillDrawers(
            $drawerWrapper,
            idNum,
            vizDataStored,
            buttonMode,
            serverParams.challengeType === 'team-summary'
        )
    }
}

function parseUniqueId(idStr: string) {
    const breakevenMap: Record<string, string> = {
        Low: 'A. Low',
        '5050': 'B. 50/50',
        High: 'C. High',
        VeryHigh: 'D. Very High',
    }

    const groupByProperties = serverParams.secondaryGroupBy
        // This must match the server side
        .filter((d) => d !== 'api_game_date_month_text')
        .sort((a, b) => (a > b ? 1 : -1))

    const [id, ...groupBys] = idStr.split('_')

    if (groupByProperties.length !== groupBys.length) {
        throw new Error('mismatch in groups')
    }

    const uniqueData: Record<string, string> = { id }
    groupByProperties.forEach((prop, i) => {
        if (prop !== 'abschallenge_breakeven_code') {
            uniqueData[prop] = groupBys[i]
        } else {
            uniqueData[prop] = breakevenMap[groupBys[i]]
        }
    })

    return uniqueData
}

if (serverParams.challengeType !== 'league') {
    document
        .getElementById(tableDivId)
        ?.addEventListener('click', async (e) => {
            const $target = e.target as HTMLElement
            if (!$target) {
                return
            }

            const $button = $target.closest('.drawer-nav') as HTMLElement
            if ($button) {
                handleTabSwitch($button)
                return
            }

            const $el = $target.closest('tr.default-table-row') as HTMLElement
            if (!$el || $el.classList.contains('inner-table')) {
                return
            }

            const $sub = $el.nextSibling as HTMLElement | null
            if (!$sub) {
                return
            }

            const $td = $sub.querySelector('td')
            const $wrapperDiv = document.createElement('div')

            const id = `data_${$el.dataset.id}`
            const idNum = $el.dataset.id as string
            const $innerDiv = document.getElementById(id)

            // CHeck if already exists
            if ($innerDiv) {
                if ($sub.dataset.open === 'false') {
                    slideDown($innerDiv, 400)
                    $sub.dataset.open = 'true'
                    return
                }
                slideUp($innerDiv, 400)
                $sub.dataset.open = 'false'
                return
            }

            $wrapperDiv.classList.add('table-drawer')
            $wrapperDiv.id = id
            $wrapperDiv.textContent = `Loading...`

            $sub.style.display = 'table-row'

            $td?.appendChild($wrapperDiv)

            const idForFetch = $el.dataset.id || ''
            const params = new URLSearchParams(window.location.search)
            const unique = parseUniqueId(idNum)

            Object.keys(unique).forEach((key) => {
                params.set(key, unique[key])
            })

            params.set('groupBy', '')

            const vizData = await getDrawerPitches(params, idForFetch)
            vizDataStored = vizData

            $wrapperDiv.textContent = ''

            addDrawerTabs($wrapperDiv, idNum)
            fillDrawers(
                $wrapperDiv,
                idNum,
                vizData,
                'for',
                serverParams.challengeType === 'team-summary'
            )

            slideDown($wrapperDiv, 400)
            $td?.appendChild($wrapperDiv)
            $sub.dataset.open = 'true'
        })
}
